'use client'

import { Check, Languages } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { AvailableLocale, availableLocales } from '@/i18n'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import { SettingsActions } from '@/redux/reducers/settings'

const translationMap: Record<AvailableLocale, string> = {
  en: 'English',
  sv: 'Svenska',
}

export const LocalePicker = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const locale = useAppSelector(state => state.settings.locale)

  const handleLocaleChange = (newLocale: AvailableLocale | 'auto') => {
    dispatch(SettingsActions.setLocale(newLocale))
  }

  const getAutoLabel = () => {
    if (typeof navigator === 'undefined') return 'Auto'
    const detected = navigator.language.split('-')[0]

    return `Auto (${translationMap[detected as AvailableLocale] || detected})`
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button aria-label={t('global.selectLanguage')} className="capitalize" variant="outline">
          <Languages className="h-4 w-4" />
          {locale === 'auto' ? getAutoLabel() : translationMap[locale] || locale}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => handleLocaleChange('auto')}>
          <span className={locale === 'auto' ? 'font-bold' : ''}>{getAutoLabel()}</span>
          {locale === 'auto' && <Check className="ml-2 h-4 w-4" />}
        </DropdownMenuItem>
        {availableLocales.map(loc => {
          return (
            <DropdownMenuItem key={loc} onClick={() => handleLocaleChange(loc)}>
              <span className={locale === loc ? 'font-bold' : ''}>
                {translationMap[loc] ?? loc}
              </span>
              {locale === loc && <Check className="ml-2 h-4 w-4" />}
            </DropdownMenuItem>
          )
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
